import React, { useState, useEffect } from 'react';
import {
  Box, TextField, Button, Grid, Typography, Paper,
  FormControlLabel, Checkbox, FormGroup, MenuItem,
  Autocomplete, Chip
} from '@mui/material';

import { useFormik } from 'formik';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import { apigetPublic, apipostFormData } from "../../service/api";

const titleOptions = [
  'Mortgage Loan Officer',
  'Branch Manager, Mortgage Loan Officer',
  'Area Manager, Mortgage Loan Officer'
];

const validationSchema = yup.object({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  title: yup.string().required('Title is required'),
  email: yup.string().email('Invalid email').required('Email is required'),
  phone: yup.string().required('Phone is required'),
  nmlsId: yup.string().required('NMLS ID is required'),
  slug: yup.string().required('Slug is required'),

  additionalLicensing: yup.string().when('states', {
    is: (states) => states.includes('Georgia'),
    then: (schema) => schema.required('Georgia requires additional licensing'),
    otherwise: (schema) => schema.notRequired()
  }),
});

const LoanOfficerCreateForm = () => {
  const DEFAULT_BIO = `At Jet HomeLoans, we’re dedicated to providing you with top-flight resources and first-class service to help you reach your dreams of homeownership. Comprised of some of the top mortgage talent in the industry, our team of loan professionals is committed to guiding you through every step of the home financing process.`;

  const [imageFile, setImageFile] = useState(null);
  const [branchImageFile, setBranchImageFile] = useState(null);

  // 🔥 Dynamic state + branch data
  const [statesList, setStatesList] = useState([]);
  const [branchesList, setBranchesList] = useState([]);

  useEffect(() => {
    const loadStates = async () => {
      const res = await apigetPublic("states/search");
      if (res?.status === 200) setStatesList(res.data.result);
    };

    const loadBranches = async () => {
      const res = await apigetPublic("branches/search");
      if (res?.status === 200) setBranchesList(res.data.result);
    };

    loadStates();
    loadBranches();
  }, []);

  const formik = useFormik({
    initialValues: {
      firstName: '', lastName: '', title: '', email: '', phone: '',
      nmlsId: '', slug: '', bio: '',
      applyLink: '', googleReviewLink: '', linkedinUrl: '',
      facebook: '', instagram: '', tiktok: '', twitter: '',
      additionalLicensing: '', states: [], branches: [], stateOptionsSelected: [] ,groups: []
    },
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        const formData = new FormData();
        const payload = {
          ...values,
          bio: values.bio.trim() || DEFAULT_BIO,
        };

        Object.entries(payload).forEach(([key, val]) => {
          if (Array.isArray(val)) {
            val.forEach((v, i) => formData.append(`${key}[${i}]`, v));
          } else {
            formData.append(key, val);
          }
        });

        if (imageFile) formData.append("headshot", imageFile);
        if (branchImageFile) formData.append("branchImage", branchImageFile);

        const result = await apipostFormData('loanofficer', formData);

        if (result?.status === 201) {
          toast.success('Loan Officer created successfully!');
          resetForm();
          setImageFile(null);
          setBranchImageFile(null);
        } else {
          toast.error(result?.data?.message || 'Something went wrong.');
        }
      } catch (err) {
        toast.error('Unexpected error submitting form.');
        console.error(err);
      }
    }
  });

   const handleOptionCheckbox = (option, stateName, checked) => {
  let updated = [...formik.values.stateOptionsSelected];

  const key = `${stateName}::${option}`; // unique pair

  if (checked) {
    updated.push(key);
  } else {
    updated = updated.filter((x) => x !== key);
  }

  formik.setFieldValue("stateOptionsSelected", updated);
};


  const handleCheckboxChange = (e, fieldName) => {
    const { value, checked } = e.target;
    const updated = checked
      ? [...formik.values[fieldName], value]
      : formik.values[fieldName].filter((item) => item !== value);
    formik.setFieldValue(fieldName, updated);
  };

  return (
    <Paper elevation={3} sx={{ p: 4, maxWidth: 900, mx: 'auto', mt: 8 }}>
      <Typography variant="h5" fontWeight="bold" mb={3}>
        Create New Loan Officer
      </Typography>

      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={2}>
          {/* First Name, Last Name */}
          {['firstName', 'lastName'].map((field) => (
            <Grid item xs={12} sm={6} key={field}>
              <TextField
                fullWidth
                name={field}
                label={field.replace(/([A-Z])/g, ' $1')}
                value={formik.values[field]}
                onChange={(e) => {
                  formik.handleChange(e);
                  if (field === 'firstName' || field === 'lastName') {
                    const { firstName, lastName } = {
                      ...formik.values,
                      [field]: e.target.value
                    };
                    const slug = `${firstName[0] || ''}${lastName}`.toLowerCase().replace(/[^a-z0-9]/gi, '');
                    formik.setFieldValue('slug', slug);
                  }
                }}
                error={formik.touched[field] && Boolean(formik.errors[field])}
                helperText={formik.touched[field] && formik.errors[field]}
              />
            </Grid>
          ))}

          {/* Title Dropdown */}
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              name="title"
              label="Title"
              value={formik.values.title}
              onChange={formik.handleChange}
              error={formik.touched.title && Boolean(formik.errors.title)}
              helperText={formik.touched.title && formik.errors.title}
            >
              {titleOptions.map((title) => (
                <MenuItem key={title} value={title}>
                  {title}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Email */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="email"
              label="Email"
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
            />
          </Grid>

          {/* Phone and NMLS */}
          {['phone', 'nmlsId'].map((field) => (
            <Grid item xs={12} sm={6} key={field}>
              <TextField
                fullWidth
                name={field}
                label={field === 'nmlsId' ? 'NMLS ID' : 'Phone'}
                value={formik.values[field]}
                onChange={formik.handleChange}
                error={formik.touched[field] && Boolean(formik.errors[field])}
                helperText={formik.touched[field] && formik.errors[field]}
              />
            </Grid>
          ))}

          {/* Slug */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              name="slug"
              label="Slug"
              value={formik.values.slug}
              onChange={formik.handleChange}
              error={formik.touched.slug && Boolean(formik.errors.slug)}
              helperText={formik.touched.slug && formik.errors.slug}
            />
          </Grid>

          {/* Bio */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={4}
              name="bio"
              label="Bio"
              value={formik.values.bio}
              onChange={formik.handleChange}
            />
          </Grid>
 {/* 🔥 GROUPS (MATCHES EDIT MODAL) */}
          <Grid item xs={12}>
            <Typography variant="h6">Groups</Typography>
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={formik.values.groups}
              onChange={(e, newValue) =>
                formik.setFieldValue('groups', newValue)
              }
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip label={option} {...getTagProps({ index })} key={option} />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Type a group and press Enter"
                  helperText="Example: veterans, first-time-buyers, investors"
                />
              )}
            />
          </Grid>
          {/* Links */}
          {[
            ['applyLink', 'Apply Now Link'],
            ['googleReviewLink', 'Google Review Link'],
            ['linkedinUrl', 'LinkedIn URL'],
            ['facebook', 'Facebook URL'],
            ['instagram', 'Instagram URL'],
            ['tiktok', 'TikTok URL'],
            ['twitter', 'Twitter URL']
          ].map(([name, label]) => (
            <Grid item xs={12} sm={6} key={name}>
              <TextField
                fullWidth
                name={name}
                label={label}
                value={formik.values[name]}
                onChange={formik.handleChange}
              />
            </Grid>
          ))}

          {/* Georgia Licensing */}
         {formik.values.states.includes('Georgia') && (
  <Grid item xs={12}>
    <TextField
      fullWidth
      name="additionalLicensing"
      label="Additional State Licensing (for GA)"
      value={formik.values.additionalLicensing}
      onChange={formik.handleChange}
      error={
        formik.touched.additionalLicensing &&
        Boolean(formik.errors.additionalLicensing)
      }
      helperText={
        formik.touched.additionalLicensing &&
        formik.errors.additionalLicensing
      }
    />
  </Grid>
)}


          {/* Images */}
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2">Upload Headshot</Typography>
            <input type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files[0])} />
          </Grid>
          

         {/* 🔥 DYNAMIC STATES WITH ADDITIONAL OPTION CHECKBOXES */}
<Grid item xs={12}>
  <Typography variant="subtitle1" fontWeight="bold">Select States</Typography>

  <FormGroup>
    {statesList.map((state) => {
      const isSelected = formik.values.states.includes(state.name);

      return (
        <Box key={state._id} sx={{ mb: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isSelected}
                onChange={(e) => handleCheckboxChange(e, 'states')}
                value={state.name}
              />
            }
            label={`${state.name} (${state.code})`}
          />

          {/* Additional Options BELOW state */}
          {isSelected && state.additionalOptions?.length > 0 && (
            <Box sx={{ ml: 5 }}>
              {state.additionalOptions.map((option) => {
                const key = `${state.name}::${option}`;
                const isChecked = formik.values.stateOptionsSelected.includes(key);

                return (
                  <FormControlLabel
                    key={option}
                    control={
                      <Checkbox
                        checked={isChecked}
                        onChange={(e) =>
                          handleOptionCheckbox(option, state.name, e.target.checked)
                        }
                      />
                    }
                    label={option}
                  />
                );
              })}
            </Box>
          )}
        </Box>
      );
    })}
  </FormGroup>
</Grid>


        {/* 🔥 MULTI-SELECT BRANCHES (save names instead of IDs) */}
<Grid item xs={12}>
  <Typography variant="subtitle1" fontWeight="bold">Select Branches</Typography>

  <FormGroup>
    {branchesList.map((branch) => {
      const branchValue = branch.name; // SAVE NAME, NOT ID
      const isChecked = formik.values.branches.includes(branchValue);

      return (
        <FormControlLabel
          key={branch._id}
          control={
            <Checkbox
              checked={isChecked}
              onChange={(e) => {
                const { checked } = e.target;
                let updated = [...formik.values.branches];

                if (checked) updated.push(branchValue);
                else updated = updated.filter((b) => b !== branchValue);

                formik.setFieldValue("branches", updated);
              }}
            />
          }
          label={`${branch.name} — ${branch.city}, ${branch.stateCode}`}
        />
      );
    })}
  </FormGroup>
</Grid>

          {/* Submit */}
          <Grid item xs={12}>
            <Button type="submit" variant="contained" fullWidth>
              Submit
            </Button>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default LoanOfficerCreateForm;
